import React, { useEffect, useRef, useState } from "react";
import { api, formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { StatusBadge } from "@/components/Brand";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { QrCode, ShieldCheck } from "lucide-react";

/**
 * Generic Make Payment page (used by Merchant / Vendor / Employee).
 * - Shows Active QR(s) uploaded by Admin.
 * - User fills a submission form (identity auto-filled + locked).
 * - Duplicate TXN/RRN prevented server-side.
 */
export function MakePaymentBody() {
  const { user } = useAuth();
  const [qrs, setQrs] = useState([]);
  const [services, setServices] = useState([]);
  const [selectedQr, setSelectedQr] = useState(null);
  const [history, setHistory] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const [form, setForm] = useState({
    service_id: "",
    service_name: "",
    amount: "",
    txn_id: "",
    rrn_number: "",
    payment_date: new Date().toISOString().slice(0, 10),
    screenshot_url: "",
    remark: "",
  });

  const loadHistory = () => api.get("/payment-requests").then((r) => setHistory(r.data));

  useEffect(() => {
    api.get("/payment-qrs/active").then((r) => {
      setQrs(r.data);
      if (r.data.length > 0) setSelectedQr(r.data[0]);
    });
    api.get("/services").then((r) => setServices((r.data || []).filter((s) => s.active !== false)));
    loadHistory();
  }, []);

  const handleFile = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    if (!file.type.startsWith("image/")) return toast.error("Only image files");
    if (file.size > 2 * 1024 * 1024) return toast.error("Max 2 MB");
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/uploads/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, screenshot_url: `${process.env.REACT_APP_BACKEND_URL}${data.url}` }));
      toast.success("Screenshot uploaded");
    } catch (err) { toast.error(formatError(err)); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.service_name) return toast.error("Service select करें");
    if (!form.amount || Number(form.amount) <= 0) return toast.error("Valid amount डालें");
    try {
      await api.post("/payment-requests", {
        service_id: form.service_id || null,
        service_name: form.service_name,
        amount: Number(form.amount),
        txn_id: form.txn_id.trim(),
        rrn_number: form.rrn_number.trim(),
        payment_date: form.payment_date,
        screenshot_url: form.screenshot_url || null,
        remark: form.remark || null,
        qr_id: selectedQr?.id || null,
      });
      toast.success("Payment submitted — Admin approval का wait करें");
      setForm({
        service_id: "", service_name: "", amount: "", txn_id: "", rrn_number: "",
        payment_date: new Date().toISOString().slice(0, 10), screenshot_url: "", remark: "",
      });
      loadHistory();
    } catch (err) { toast.error(formatError(err)); }
  };

  const onServiceChange = (sid) => {
    const s = services.find((x) => x.id === sid);
    setForm((f) => ({
      ...f, service_id: sid, service_name: s?.name || "",
      amount: s?.charge_type === "paid" ? String(s.charge_amount || "") : f.amount,
    }));
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      {/* Left: QR display */}
      <div className="lg:col-span-1 bg-white border rounded-xl p-5 space-y-4">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-brand-gradient text-white flex items-center justify-center"><QrCode className="h-4 w-4" /></div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500">Scan & Pay</div>
            <div className="font-display font-semibold">Active Payment QR</div>
          </div>
        </div>

        {qrs.length === 0 ? (
          <div className="text-sm text-slate-500 border rounded-lg p-4 bg-slate-50">
            Admin ने अभी कोई Payment QR active नहीं किया है. कृपया बाद में try करें.
          </div>
        ) : (
          <>
            {qrs.length > 1 && (
              <Select value={selectedQr?.id || ""} onValueChange={(v) => setSelectedQr(qrs.find((q) => q.id === v))}>
                <SelectTrigger data-testid="mp-qr-select"><SelectValue /></SelectTrigger>
                <SelectContent>{qrs.map((q) => <SelectItem key={q.id} value={q.id}>{q.name}</SelectItem>)}</SelectContent>
              </Select>
            )}
            {selectedQr && (
              <div className="text-center">
                <img src={selectedQr.qr_image_url} alt={selectedQr.name} className="w-full max-w-[240px] mx-auto rounded-xl border-2 border-slate-200 bg-white p-2" data-testid="mp-qr-image" />
                <div className="mt-3 font-display font-semibold">{selectedQr.name}</div>
                <div className="text-xs text-slate-500 font-mono break-all" data-testid="mp-qr-upi">{selectedQr.upi_id}</div>
              </div>
            )}
          </>
        )}

        <div className="pt-3 border-t text-xs text-slate-500 flex items-start gap-2">
          <ShieldCheck className="h-4 w-4 text-emerald-600 shrink-0 mt-0.5" />
          <span>पहले payment complete करें फिर नीचे form में TXN ID / RRN number भरें. Duplicate TXN block हो जाएगा.</span>
        </div>
      </div>

      {/* Right: Submission form */}
      <form onSubmit={submit} className="lg:col-span-2 bg-white border rounded-xl p-5 grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="md:col-span-2 text-[10px] uppercase tracking-widest text-slate-500 font-medium">Your Identity (Auto-filled, cannot change)</div>
        <div><Label>Agent Name</Label><Input value={user?.name || ""} readOnly disabled data-testid="mp-user-name" /></div>
        <div><Label>User ID</Label><Input value={user?.id || ""} readOnly disabled className="font-mono text-xs" data-testid="mp-user-id" /></div>
        <div><Label>Mobile</Label><Input value={user?.mobile || ""} readOnly disabled data-testid="mp-user-mobile" /></div>
        <div><Label>Role</Label><Input value={user?.role || ""} readOnly disabled className="uppercase" data-testid="mp-user-role" /></div>

        <div className="md:col-span-2 mt-3 text-[10px] uppercase tracking-widest text-slate-500 font-medium">Payment Details</div>
        <div className="md:col-span-2"><Label>Service *</Label>
          <Select value={form.service_id} onValueChange={onServiceChange}>
            <SelectTrigger data-testid="mp-service"><SelectValue placeholder="Select service you paid for" /></SelectTrigger>
            <SelectContent>{services.map((s) => <SelectItem key={s.id} value={s.id}>{s.name} {s.charge_type === "paid" ? `— ₹${Number(s.charge_amount || 0).toFixed(0)}` : "(Free)"}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div><Label>Amount Paid (₹) *</Label><Input required type="number" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="mp-amount" /></div>
        <div><Label>Payment Date *</Label><Input required type="date" value={form.payment_date} onChange={(e) => setForm({ ...form, payment_date: e.target.value })} data-testid="mp-date" /></div>
        <div><Label>TXN / UTR ID *</Label><Input required value={form.txn_id} onChange={(e) => setForm({ ...form, txn_id: e.target.value })} placeholder="Bank/UPI transaction ID" data-testid="mp-txn" /></div>
        <div><Label>RRN Number *</Label><Input required value={form.rrn_number} onChange={(e) => setForm({ ...form, rrn_number: e.target.value })} placeholder="Reference (RRN) number" data-testid="mp-rrn" /></div>

        <div className="md:col-span-2">
          <Label>Payment Screenshot</Label>
          <div className="mt-1.5 flex items-center gap-3">
            {form.screenshot_url && <img src={form.screenshot_url} alt="ss" className="h-16 w-16 object-cover rounded border" />}
            <input ref={fileRef} type="file" accept="image/*" onChange={handleFile} className="hidden" data-testid="mp-ss-file" />
            <Button type="button" variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="mp-ss-btn">
              {uploading ? "Uploading…" : form.screenshot_url ? "Change" : "Upload Screenshot"}
            </Button>
            {form.screenshot_url && <Button type="button" variant="ghost" className="text-rose-600" onClick={() => setForm({ ...form, screenshot_url: "" })}>Remove</Button>}
          </div>
        </div>

        <div className="md:col-span-2"><Label>Remark (optional)</Label><Textarea value={form.remark} onChange={(e) => setForm({ ...form, remark: e.target.value })} data-testid="mp-remark" /></div>

        <div className="md:col-span-2 flex justify-end">
          <Button type="submit" disabled={qrs.length === 0} className="bg-brand-gradient hover:opacity-90 text-white rounded-full px-6" data-testid="mp-submit">Submit Payment</Button>
        </div>
      </form>

      {/* History */}
      <div className="lg:col-span-3">
        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-medium mb-2">My Payment Submissions</div>
        <div className="bg-white border rounded-xl overflow-x-auto">
          <Table>
            <TableHeader><TableRow><TableHead>Service</TableHead><TableHead>Amount</TableHead><TableHead>TXN ID</TableHead><TableHead>RRN</TableHead><TableHead>Date</TableHead><TableHead>Status</TableHead><TableHead>Reason</TableHead></TableRow></TableHeader>
            <TableBody>
              {history.map((p) => (
                <TableRow key={p.id} data-testid={`mp-hist-${p.id}`}>
                  <TableCell>{p.service_name}</TableCell>
                  <TableCell>₹ {Number(p.amount).toFixed(2)}</TableCell>
                  <TableCell className="font-mono text-xs">{p.txn_id}</TableCell>
                  <TableCell className="font-mono text-xs">{p.rrn_number}</TableCell>
                  <TableCell className="text-xs text-slate-500">{p.payment_date}</TableCell>
                  <TableCell><StatusBadge status={p.status} /></TableCell>
                  <TableCell className="text-xs text-slate-500">{p.reject_reason || "—"}</TableCell>
                </TableRow>
              ))}
              {history.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-slate-500 py-6">No submissions yet</TableCell></TableRow>}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
}
